import { DragEvent, useId, useRef, useState } from "react";
import { Box } from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

import { ACCEPTED_TYPES, MAX_BYTES } from "../DigitalSignature/wetInkScan";

// The deed-scan dropzone from the Figma anchor wizard (node 1719:4377).
//
// Visually this is the KYC flow's FileDropzone (pages/BecomeTrust/kyc/FileDropzone.tsx) — same
// 2px dashed #3d3d3d on #f3f3f3, same radius 20, same 44px cloud glyph and "Drag and drop • …"
// line — but it is deliberately not that component. Anchoring hashes the raw bytes
// (keccak256 of file.arrayBuffer()), while FileDropzone's API hands back a base64 dataUrl, and
// round-tripping through base64 to recover bytes is a good way to anchor the wrong hash. It also
// carries a CameraCapture dialog this flow has no use for, and lives under pages/, which
// components/ should not be importing from. Folding the two together is worth doing separately.

// Figma reads "JPG, or PNG" but validateScan also accepts PDF, so the hint is derived from the
// real allow-list rather than transcribed — copy that contradicts the validator is a bug.
const EXTENSION_BY_TYPE: Record<string, string> = {
  "application/pdf": "PDF",
  "image/jpeg": "JPG",
  "image/png": "PNG",
};

const describeAccepted = (types: string[]): string => {
  const names = types.map((type) => EXTENSION_BY_TYPE[type] ?? type.split("/")[1]?.toUpperCase()).filter(Boolean);
  const last = names.pop();
  const list = names.length ? `${names.join(", ")}, or ${last}` : last;
  return `Drag and drop • ${list} • Max ${Math.round(MAX_BYTES / (1024 * 1024))} MB`;
};

export interface DeedDropzoneProps {
  file: File | null;
  onFile: (file: File) => void;
  disabled?: boolean;
  // MIME types the picker offers and the hint lists. Defaults to everything wet-ink verification
  // takes; a flow that wants less (POAAnchor is PDF-only) narrows it here. This only steers the
  // picker — a dropped file is not filtered, so the host must still validate what onFile receives.
  acceptedTypes?: string[];
}

const DeedDropzone = ({ file, onFile, disabled, acceptedTypes = ACCEPTED_TYPES }: DeedDropzoneProps) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) {
      return;
    }
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) {
      onFile(dropped);
    }
  };

  // The selected file reads as a chip rather than the full zone, matching the drawn
  // verification-failed screen where the chosen scan sits above the deed hash.
  if (file) {
    return (
      <Box
        component="label"
        htmlFor={inputId}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          minHeight: 48,
          padding: "12px 16px",
          border: "1px dashed #3d3d3d",
          borderRadius: "20px",
          backgroundColor: "#f3f3f3",
          cursor: disabled ? "default" : "pointer",
          fontFamily: "'Inter', sans-serif",
          fontSize: "14px",
          color: "#000",
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, flexShrink: 0 }} />
        <Box
          component="span"
          sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={file.name}
        >
          {file.name}
        </Box>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={acceptedTypes.join(",")}
          hidden
          disabled={disabled}
          onChange={(e) => {
            const picked = e.target.files?.[0];
            if (picked) {
              onFile(picked);
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      component="label"
      htmlFor={inputId}
      onDrop={handleDrop}
      onDragOver={(e: DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        if (!disabled) {
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: "24px 20px",
        border: `2px dashed ${dragging ? "#000" : "#3d3d3d"}`,
        borderRadius: "20px",
        backgroundColor: dragging ? "rgba(0, 0, 0, 0.04)" : "#f3f3f3",
        cursor: disabled ? "default" : "pointer",
        textAlign: "center",
        transition: "border-color 0.2s, background-color 0.2s",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <CloudUploadRoundedIcon sx={{ fontSize: 44, color: "#000" }} />
      <Box
        component="span"
        sx={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontWeight: 700,
          fontSize: "16px",
          lineHeight: "19.2px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: "#000",
        }}
      >
        Upload document
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "10px",
          lineHeight: "19.2px",
          textTransform: "uppercase",
          color: "#000",
        }}
      >
        {describeAccepted(acceptedTypes)}
      </Box>
      <Box
        component="span"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "80%",
          minWidth: 64,
          height: 44,
          marginTop: "10px",
          padding: "4px 10px",
          border: "1px solid rgba(0, 0, 0, 0.5)",
          borderRadius: "16px",
          fontFamily: "'Inter', sans-serif",
          fontSize: "12px",
          lineHeight: "21px",
          textTransform: "uppercase",
          color: "#000",
        }}
      >
        Choose file
      </Box>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={acceptedTypes.join(",")}
        hidden
        disabled={disabled}
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) {
            onFile(picked);
          }
        }}
      />
    </Box>
  );
};

export default DeedDropzone;
